import React, { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import { formatDate } from "../lib/format";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const STATUS_COLORS = {
  Approved: "bg-emerald-700 text-white",
  Rejected: "bg-[#D0021B] text-white",
  Revision: "bg-[#F5A623] text-black",
  Pending: "bg-zinc-700 text-white",
};

export default function Approvals() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decisionDialog, setDecisionDialog] = useState(null);
  const [createDialog, setCreateDialog] = useState(false);
  const [decisionNotes, setDecisionNotes] = useState("");
  const [decision, setDecision] = useState("Approved");
  const { user } = useAuth();
  const canApprove = user && (user.role === "admin" || user.role === "approver");
  const canCreate = user && (user.role === "admin" || user.role === "credit_officer");

  const [form, setForm] = useState({
    request_type: "limit_increase",
    target_facility_id: "",
    notes: "",
    new_limit: 0,
  });

  const load = () => {
    setLoading(true);
    api.get("/approvals").then((r) => setData(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- load once on mount

  const handleDecision = async () => {
    try {
      await api.post(`/approvals/${decisionDialog.approval_id}/decision`, { decision, notes: decisionNotes });
      toast.success(`Request ${decision.toLowerCase()}`);
      setDecisionDialog(null);
      setDecisionNotes("");
      load();
    } catch (err) {
      toast.error("Gagal memproses keputusan");
    }
  };

  const handleCreate = async () => {
    try {
      await api.post("/approvals", {
        request_type: form.request_type,
        target_facility_id: form.target_facility_id,
        notes: form.notes,
        payload: { new_limit: parseFloat(form.new_limit) || 0 },
      });
      toast.success("Request approval dibuat");
      setCreateDialog(false);
      setForm({ request_type: "limit_increase", target_facility_id: "", notes: "", new_limit: 0 });
      load();
    } catch (err) {
      toast.error("Gagal membuat request");
    }
  };

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="approvals-page">
      <PageHeader
        title="Approval Workflow"
        subtitle={`${data.length} approval request`}
        action={canCreate && (
          <Button data-testid="add-approval-button" onClick={() => setCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-1" /> Buat Request
          </Button>
        )}
      />

      {loading ? <div className="text-sm text-muted-foreground">Memuat…</div> : (
        <DataTable
          testId="approvals-table"
          searchKeys={["approval_id", "request_type", "target_facility_id"]}
          columns={[
            { header: "Approval ID", cell: (r) => <span className="mono">{r.approval_id}</span> },
            { header: "Request Type", cell: (r) => <span className="text-xs">{r.request_type}</span> },
            { header: "Facility", cell: (r) => <span className="mono text-xs">{r.target_facility_id || "-"}</span> },
            { header: "Requested By", cell: (r) => (
              <div>
                <div className="text-xs font-medium">{r.requested_by_name}</div>
                <div className="text-[10px] text-muted-foreground mono">{r.requested_by}</div>
              </div>
            ) },
            { header: "Approved By", cell: (r) => r.approved_by_name ? (
              <div>
                <div className="text-xs font-medium">{r.approved_by_name}</div>
                <div className="text-[10px] text-muted-foreground mono">{formatDate(r.approval_date)}</div>
              </div>
            ) : <span className="text-xs text-muted-foreground">-</span> },
            { header: "Notes", cell: (r) => <span className="text-xs">{r.notes}</span> },
            { header: "Status", cell: (r) => <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold uppercase rounded ${STATUS_COLORS[r.approval_status] || ""}`}>{r.approval_status}</span> },
            ...(canApprove ? [{
              header: "Aksi", cell: (r) => r.approval_status === "Pending" ? (
                <Button size="sm" variant="outline" onClick={() => setDecisionDialog(r)} data-testid={`decide-${r.approval_id}`}>Putuskan</Button>
              ) : null
            }] : []),
          ]}
          data={data}
        />
      )}

      {/* Decision dialog */}
      <Dialog open={!!decisionDialog} onOpenChange={(o) => !o && setDecisionDialog(null)}>
        <DialogContent data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle>Keputusan Approval</DialogTitle>
          </DialogHeader>
          {decisionDialog && (
            <div className="space-y-4">
              <div className="text-sm">
                <div><span className="text-muted-foreground">Approval ID:</span> <span className="mono">{decisionDialog.approval_id}</span></div>
                <div><span className="text-muted-foreground">Tipe:</span> {decisionDialog.request_type}</div>
                <div><span className="text-muted-foreground">Facility:</span> <span className="mono">{decisionDialog.target_facility_id}</span></div>
                {decisionDialog.payload?.new_limit && (
                  <div><span className="text-muted-foreground">New Limit:</span> <span className="mono">{decisionDialog.payload.new_limit?.toLocaleString("id-ID")}</span></div>
                )}
              </div>
              <div>
                <Label>Keputusan</Label>
                <Select value={decision} onValueChange={setDecision}>
                  <SelectTrigger data-testid="decision-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Approved">Approved</SelectItem>
                    <SelectItem value="Rejected">Rejected</SelectItem>
                    <SelectItem value="Revision">Request Revision</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={decisionNotes} onChange={(e) => setDecisionNotes(e.target.value)} data-testid="decision-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionDialog(null)}>Batal</Button>
            <Button onClick={handleDecision} data-testid="decision-submit">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent data-testid="approval-create-dialog">
          <DialogHeader><DialogTitle>Buat Approval Request</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipe Request</Label>
              <Select value={form.request_type} onValueChange={(v) => setForm({ ...form, request_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new_facility">New Facility</SelectItem>
                  <SelectItem value="limit_increase">Limit Increase</SelectItem>
                  <SelectItem value="limit_decrease">Limit Decrease</SelectItem>
                  <SelectItem value="limit_renewal">Limit Renewal</SelectItem>
                  <SelectItem value="collateral_change">Collateral Change</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Target Facility ID</Label>
              <Input value={form.target_facility_id} onChange={(e) => setForm({ ...form, target_facility_id: e.target.value })} placeholder="FAC-0001" data-testid="approval-facility-input" />
            </div>
            <div>
              <Label>New Limit (untuk limit_*)</Label>
              <Input type="number" value={form.new_limit} onChange={(e) => setForm({ ...form, new_limit: e.target.value })} />
            </div>
            <div>
              <Label>Catatan</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Batal</Button>
            <Button onClick={handleCreate} data-testid="approval-create-submit">Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
